import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  apiGetEnquiries,
  apiDeleteEnquiry,
  apiUpdateEnquiryStatus,
} from '../api/enquiries/enquiriesApi'
import { enquiryEntries as seedEnquiries } from '../data/enquiries'
import { USE_DASHBOARD_REAL_BACKEND } from '../api/config/config'
import type { EnquiryRecord, EnquiryStatus } from '../types'

export type EnquiryFilterStatus = 'all' | 'open' | 'replied' | 'closed'

export const useEnquiries = () => {
  const [enquiries, setEnquiries] = useState<EnquiryRecord[]>([])
  const [filterStatus, setFilterStatus] = useState<EnquiryFilterStatus>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedEnquiry, setSelectedEnquiry] = useState<EnquiryRecord | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [latestIncoming, setLatestIncoming] = useState<EnquiryRecord | null>(null)

  const loadEnquiries = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    // Check local storage entries first
    let localList: EnquiryRecord[] = []
    try {
      const raw = localStorage.getItem('salzburg_enquiry_entries')
      if (raw) localList = JSON.parse(raw)
    } catch {}

    if (!USE_DASHBOARD_REAL_BACKEND) {
      // In mock mode, combine seed and local
      const seenIds = new Set(localList.map((e) => String(e.id)))
      const combined = [...localList]
      for (const seed of seedEnquiries) {
        if (!seenIds.has(String(seed.id))) {
          combined.push(seed)
        }
      }
      setEnquiries(combined)
      setIsLoading(false)
      return
    }

    try {
      const res = await apiGetEnquiries()
      if (res.success && Array.isArray(res.data)) {
        // If local entries exist, merge them at the top
        const seenIds = new Set(res.data.map((e) => String(e.id)))
        const merged = [...res.data]
        for (const local of localList) {
          if (!seenIds.has(String(local.id))) {
            merged.unshift(local)
            seenIds.add(String(local.id))
          }
        }
        setEnquiries(merged)
      } else {
        // Fallback to local entries or seed
        setEnquiries(localList.length > 0 ? localList : seedEnquiries)
        if (!res.success && res.message) {
          console.warn('apiGetEnquiries message:', res.message)
        }
      }
    } catch {
      setEnquiries(localList.length > 0 ? localList : seedEnquiries)
      setError('Unable to reach backend, using local enquiries.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadEnquiries()
  }, [loadEnquiries])

  // Listen for realtime events (e.g. user submitting an enquiry modal in frontend)
  useEffect(() => {
    const handleNewEnquiry = (e: Event) => {
      const customEvent = e as CustomEvent<EnquiryRecord>
      if (customEvent.detail) {
        const item = customEvent.detail
        setLatestIncoming(item)
        setEnquiries((prev) => {
          if (prev.some((p) => String(p.id) === String(item.id))) return prev
          return [item, ...prev]
        })
      } else {
        loadEnquiries()
      }
    }

    const handleBadgeUpdate = () => {
      loadEnquiries()
    }

    window.addEventListener('salzburg-enquiry-created', handleNewEnquiry)
    window.addEventListener('dashboard-badges-updated', handleBadgeUpdate)
    window.addEventListener('storage', handleBadgeUpdate)

    return () => {
      window.removeEventListener('salzburg-enquiry-created', handleNewEnquiry)
      window.removeEventListener('dashboard-badges-updated', handleBadgeUpdate)
      window.removeEventListener('storage', handleBadgeUpdate)
    }
  }, [loadEnquiries])

  const setEnquiryStatus = useCallback(
    async (id: string, status: EnquiryStatus) => {
      // Optimistic update
      setEnquiries((prev) =>
        prev.map((entry) => (String(entry.id) === String(id) ? { ...entry, status } : entry))
      )
      if (selectedEnquiry && String(selectedEnquiry.id) === String(id)) {
        setSelectedEnquiry((prev) => (prev ? { ...prev, status } : null))
      }

      await apiUpdateEnquiryStatus(id, status)
    },
    [selectedEnquiry]
  )

  const removeEnquiry = useCallback(
    async (id: string) => {
      setEnquiries((prev) => prev.filter((entry) => String(entry.id) !== String(id)))
      if (selectedEnquiry && String(selectedEnquiry.id) === String(id)) {
        setSelectedEnquiry(null)
      }
      await apiDeleteEnquiry(id)
    },
    [selectedEnquiry]
  )

  // Filtered and searched list
  const filteredEnquiries = useMemo(() => {
    return enquiries.filter((item) => {
      if (filterStatus !== 'all' && item.status !== filterStatus) {
        return false
      }

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase()
        const name = item.customer?.name?.toLowerCase() || ''
        const email = item.customer?.email?.toLowerCase() || ''
        const phone = (item.customer?.phone || item.customer_phone || '').toLowerCase()
        const subject = item.subject?.toLowerCase() || ''
        const tour = (item.tour_title || '').toLowerCase()
        const message = item.message?.toLowerCase() || ''

        return (
          name.includes(q) ||
          email.includes(q) ||
          phone.includes(q) ||
          subject.includes(q) ||
          tour.includes(q) ||
          message.includes(q)
        )
      }

      return true
    })
  }, [enquiries, filterStatus, searchQuery])

  // Aggregate stats
  const stats = useMemo(() => {
    const total = enquiries.length
    const open = enquiries.filter((e) => e.status === 'open').length
    const replied = enquiries.filter((e) => e.status === 'replied').length
    const closed = enquiries.filter((e) => e.status === 'closed').length
    return { total, open, replied, closed }
  }, [enquiries])

  return {
    enquiries: filteredEnquiries,
    allEnquiries: enquiries,
    filterStatus,
    setFilterStatus,
    searchQuery,
    setSearchQuery,
    selectedEnquiry,
    setSelectedEnquiry,
    stats,
    isLoading,
    error,
    latestIncoming,
    dismissLatestIncoming: () => setLatestIncoming(null),
    refresh: loadEnquiries,
    setEnquiryStatus,
    removeEnquiry,
  }
}
